import { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useNotification } from "../context/NotificationContext";
import PasswordChangeModal from "./PasswordChangeModal";
import SystemUseNotice from "./SystemUseNotice";
import TermsDialog from "./TermsDialog";
import { api } from "../services/api";
import { endSession, needsPasswordChange, readUser } from "../services/session";
import { ssoEnabled, ssoOnly, startSso } from "../services/cognitoAuth";
import cblogo from "../images/logos/cblogo.png";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem("rememberMe") === "true";
  });
  const [loading, setLoading] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [error, setError] = useState("");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [_pendingNavigation, _setPendingNavigation] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();
  // A customer-specific link (/login?idp=AcmeEntra) sends SSO straight to that provider,
  // skipping the chooser page.
  const idp = new URLSearchParams(useLocation().search).get("idp") || undefined;
  const { updateUser } = useApp();
  const { showNotification } = useNotification();
  // Read once at mount: whether this render is the SSO hand-off from /auth/callback. A ref
  // (not state) so later re-renders can't un-decide it once the flag is consumed below, and the
  // mount-redirect effect (which re-runs whenever its dependencies' identities change) keeps
  // deferring to the SSO effect for the lifetime of this component instance.
  const isSsoHandoff = useRef(sessionStorage.getItem("sso:complete") === "1");

  useEffect(() => {
    localStorage.setItem("rememberMe", rememberMe);
  }, [rememberMe]);

  useEffect(() => {
    if (isSsoHandoff.current) {
      return;
    }
    try {
      const savedUser = localStorage.getItem("user");
      const sessionUser = sessionStorage.getItem("user");
      const user = rememberMe ? savedUser : sessionUser;

      const pending = readUser();
      if (needsPasswordChange(pending)) {
        // a stored first-login session (new window, or a 403 from the server) changes first
        setEmail(pending.email || "");
        setIsFirstLogin(true);
        setShowPasswordChange(true);
        return;
      }

      if (user) {
        const userData = JSON.parse(user);
        updateUser(userData);

        if (userData.role === "exec" || userData.role === "cbExec") {
          navigate("/executive-report");
        } else {
          navigate("/dashboard");
        }
      }
    } catch (error) {
      console.error("Error parsing user from storage:", error);
    }
  }, [navigate, updateUser, rememberMe]);

  useEffect(() => {
    if (!isSsoHandoff.current) {
      return;
    }
    sessionStorage.removeItem("sso:complete");
    const user = JSON.parse(sessionStorage.getItem("user") || "null");
    if (user) {
      afterLogin(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateUser = async (user) => {
    // Navigate based on role
    if (user.role === "exec" || user.role === "cbExec") {
      navigate("/executive-report");
    } else if (user.role === "cbAuthority") {
      navigate("/authority-reviews");
    } else if (user.role === "cbSupplier") {
      // Check supplier onboarding status
      try {
        const profile = await api.getMyProfile();
        if (profile.onboarding_status === "Completed") {
          navigate("/supplier-tasks");
        } else {
          navigate("/supplier-onboarding");
        }
      } catch (err) {
        // If profile fetch fails, default to onboarding
        navigate("/supplier-onboarding");
      }
    } else {
      navigate("/dashboard");
    }
  };

  const afterLogin = async (response) => {
    // Store user data in appropriate storage based on rememberMe
    if (rememberMe) {
      localStorage.setItem("user", JSON.stringify(response));
      sessionStorage.removeItem("user");
    } else {
      sessionStorage.setItem("user", JSON.stringify(response));
      localStorage.removeItem("user");
    }

    if (response.isfirstlogin) {
      setIsFirstLogin(true);
      setShowPasswordChange(true);
      setLoading(false);
      return;
    }

    await updateUser(response);

    // Check if user has accepted terms and conditions
    if (!response.terms_accepted) {
      // Show T&C modal and store user info for later navigation
      setCurrentUser(response);
      setShowTermsModal(true);
      setLoading(false);
      return;
    }

    // User has already accepted terms, proceed with navigation
    await navigateUser(response);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await api.login(email, password);
      await afterLogin(response);
    } catch (err) {
      showNotification(err.message || "Failed to login", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTerms = async (acceptance) => {
    if (!currentUser) {
      return;
    }

    try {
      const res = await api.acceptTerms(currentUser.user_id, acceptance);
      const accepted = { ...currentUser, terms_accepted: true, terms_accepted_at: res.terms_accepted_at };
      const storage = localStorage.getItem("user") ? localStorage : sessionStorage;
      storage.setItem("user", JSON.stringify(accepted));
      await updateUser(accepted);

      setShowTermsModal(false);
      await navigateUser(accepted);
    } catch (err) {
      showNotification(err.message || "Failed to record terms acceptance", "error");
      console.error("Failed to record terms acceptance:", err);
    }
  };

  const handleCancelTerms = () => {
    // User cancelled T&C acceptance, return to login
    setShowTermsModal(false);
    setCurrentUser(null);
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    setEmail("");
    setPassword("");
    showNotification("You must accept the Terms and Conditions to continue", "warning");
  };

  // the forced change can be abandoned only by signing out, back to the sign-in form
  const handleFirstLoginSignOut = () => {
    endSession();
    setShowPasswordChange(false);
    setIsFirstLogin(false);
    setEmail("");
    setPassword("");
  };

  const handlePasswordChangeClose = async () => {
    setShowPasswordChange(false);

    const user = readUser();

    // If it's still a first login (password change wasn't completed), show warning and reopen
    if (user && user.isfirstlogin) {
      showNotification("You must change your password before continuing", "warning");
      setShowPasswordChange(true);
      return;
    }

    // If we reach here, the password change was successful (isfirstlogin is false)
    if (user) {
      setIsFirstLogin(false); // Reset the local state
      await updateUser(user);

      // Check if user has accepted terms and conditions
      if (!user.terms_accepted) {
        // Show T&C modal and store user info for later navigation
        setCurrentUser(user);
        setShowTermsModal(true);
        return;
      }

      // User has already accepted terms, proceed with navigation
      await navigateUser(user);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#eeeffa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <img
            src={cblogo}
            alt="CertBuddy Logo"
            style={{ width: "200px", marginBottom: "2.5rem" }}
          />

          <SystemUseNotice />

          <Paper
            sx={{
              px: 5,
              py: 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              maxWidth: 500,
              mx: "auto",
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "#ffffff",
            }}
          >
            <Typography
              component="h1"
              variant="h4"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                mb: 0.5,
                letterSpacing: "-0.02em",
              }}
            >
              Sign in
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mb: 4,
                color: "text.secondary",
                fontSize: "0.95rem",
              }}
            >
              Welcome back! Please sign in to continue
            </Typography>

            {ssoEnabled() && (
              <Button
                fullWidth
                variant={ssoOnly() ? "contained" : "outlined"}
                onClick={() => startSso(idp)}
                sx={{ mb: 2, textTransform: "none" }}
              >
                Sign in with SSO
              </Button>
            )}

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  width: "100%",
                  borderRadius: 2,
                  "& .MuiAlert-message": { fontSize: "0.9rem" },
                }}
              >
                {error}
              </Alert>
            )}

            {!ssoOnly() && (
              <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{
                    mt: 0,
                    mb: 2.5,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "grey.50",
                      height: 56,
                      "& fieldset": {
                        borderColor: "divider",
                      },
                      "&:hover fieldset": {
                        borderColor: "grey.300",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#ffffff",
                        "& fieldset": {
                          borderColor: "primary.main",
                          borderWidth: 2,
                        },
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "text.secondary",
                      fontSize: "0.95rem",
                      "&.Mui-focused": {
                        color: "primary.main",
                      },
                    },
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{
                    mt: 0,
                    mb: 2.5,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "grey.50",
                      height: 56,
                      "& fieldset": {
                        borderColor: "divider",
                      },
                      "&:hover fieldset": {
                        borderColor: "grey.300",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#ffffff",
                        "& fieldset": {
                          borderColor: "primary.main",
                          borderWidth: 2,
                        },
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "text.secondary",
                      fontSize: "0.95rem",
                      "&.Mui-focused": {
                        color: "primary.main",
                      },
                    },
                  }}
                />

                <Box sx={{ mt: 1, mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        sx={{
                          color: "text.secondary",
                          "&.Mui-checked": {
                            color: "primary.main",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontWeight: 400, fontSize: "0.875rem" }}
                      >
                        Remember me
                      </Typography>
                    }
                  />
                </Box>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    mt: 1,
                    mb: 1,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    py: 1.75,
                    fontSize: "1rem",
                    height: 52,
                    backgroundColor: "primary.main",
                    boxShadow: "0 2px 4px rgba(86, 100, 210, 0.25)",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                      boxShadow: "0 4px 8px rgba(86, 100, 210, 0.35)",
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": {
                      backgroundColor: "grey.300",
                      color: "text.disabled",
                      boxShadow: "none",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  {loading ? "Signing In..." : "Sign In"}
                </Button>
              </Box>
            )}
          </Paper>
        </Box>

        <PasswordChangeModal
          open={showPasswordChange}
          onClose={handlePasswordChangeClose}
          email={email}
          isFirstLogin={isFirstLogin}
          onSignOut={handleFirstLoginSignOut}
        />

        {showTermsModal && (
          <TermsDialog open onAccept={handleAcceptTerms} onCancel={handleCancelTerms} />
        )}
      </Container>
    </Box>
  );
};

export default Login;
